import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/authStore';
import { UserRolesModal } from './PermissionsPage';

interface UserSummary {
  id: string;
  tenantId: string;
  email: string;
  role: string;
  status: string;
  locale: string;
}

const ALL_ROLES = [
  'PLATFORM_ADMIN',
  'TENANT_ADMIN',
  'SALES_MANAGER',
  'SALES_REPRESENTATIVE',
  'PRE_SALES',
  'FINANCE',
] as const;

const ROLE_COLORS: Record<string, string> = {
  PLATFORM_ADMIN: 'bg-purple-900/40 text-purple-200',
  TENANT_ADMIN: 'bg-blue-900/40 text-blue-200',
  SALES_MANAGER: 'bg-teal-900/40 text-teal-200',
  SALES_REPRESENTATIVE: 'bg-emerald-900/40 text-emerald-200',
  PRE_SALES: 'bg-amber-900/40 text-amber-200',
  FINANCE: 'bg-muted text-foreground',
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-emerald-900/40 text-emerald-200',
  DISABLED: 'bg-red-900/40 text-red-200',
};

export function UsersPage() {
  const { t } = useTranslation();
  const currentRole = useAuthStore((s) => s.user?.role);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<UserSummary | null>(null);
  const [rolesUser, setRolesUser] = useState<UserSummary | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<UserSummary[]>('/users');
      setUsers(data);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleDeactivate(id: string) {
    try {
      await apiClient.delete(`/users/${id}`);
      setToast('User deactivated');
      load();
    } catch {
      setToast('Failed to deactivate user');
    }
  }

  if (
    currentRole &&
    currentRole !== 'TENANT_ADMIN' &&
    currentRole !== 'PLATFORM_ADMIN'
  ) {
    return <p className="text-sm text-destructive">Access denied</p>;
  }

  return (
    <div className="space-y-4">
      <CreateUserModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setToast(t('users.created'));
          load();
        }}
      />

      <EditUserModal
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSaved={() => {
          setToast(t('users.updated'));
          load();
        }}
      />

      {rolesUser && (
        <UserRolesModal
          userId={rolesUser.id}
          userEmail={rolesUser.email}
          onClose={() => setRolesUser(null)}
          onSaved={() => {
            setToast(t('users.roles_updated'));
            setRolesUser(null);
            load();
          }}
        />
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('users.title')}</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          {t('users.add_user')}
        </Button>
      </div>

      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}

      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">{t('users.col_email')}</th>
              <th className="px-4 py-3">{t('users.col_role')}</th>
              <th className="px-4 py-3">{t('users.col_status')}</th>
              <th className="px-4 py-3">{t('users.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t">
                <td className="px-4 py-3 font-medium">{u.email}</td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      ROLE_COLORS[u.role] ?? 'bg-muted text-foreground'
                    }`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      STATUS_COLORS[u.status] ?? 'bg-muted text-foreground'
                    }`}
                  >
                    {u.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingUser(u)}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRolesUser(u)}
                    >
                      {t('users.manage_roles')}
                    </Button>
                    {u.status === 'ACTIVE' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeactivate(u.id)}
                      >
                        {t('users.deactivate')}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-muted-foreground"
                >
                  {t('users.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border bg-card px-4 py-3 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('SALES_REPRESENTATIVE');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/users', {
        email: email.trim(),
        password: password.trim(),
        role,
      });
      setEmail('');
      setPassword('');
      setRole('SALES_REPRESENTATIVE');
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : t('users.create_error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('users.create_title')}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t('users.email_label')} <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t('users.password_label')} <span className="text-red-500">*</span>
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('users.password_placeholder')}
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('users.role_label')}</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {ALL_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting || !email.trim() || !password.trim()}
            >
              {submitting ? t('common.create') + '…' : t('common.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserSummary | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setRole(user.role);
      setStatus(user.status);
      setError(null);
    }
  }, [user]);

  if (!user) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.patch(`/users/${user!.id}`, { role, status });
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : t('users.update_error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('users.edit_title')}</h3>
        <p className="mb-4 text-sm text-muted-foreground">{user.email}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('users.role_label')}</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {ALL_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('users.status_label')}</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="ACTIVE">ACTIVE</option>
              <option value="DISABLED">INACTIVE</option>
            </select>
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? t('common.save') + '…' : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
